import {create} from "zustand"
import type {DocumentStatus as BackendDocumentStatus, OtkDocumentCounts, Sort} from "shared"

import {
  createOtkDocument,
  deleteOtkDocument,
  getOtkDocumentById,
  getOtkDocumentCounts,
  getOtkDocumentsPage,
  patchOtkDocument,
  retryOtkDocument,
  saveOtkDocumentNonconformities,
} from "@/lib/api/documents"
import {buildDuplicateNonconformitiesPayload, buildDuplicatePatchOperations,} from "@/lib/documentEditor"
import {
  type DocumentRow,
  type DocumentTabStatus,
  documentTabStatuses,
  documentTabStatusToBackend,
  mapOtkDocumentView,
  sortDocumentsByCreated,
} from "@/lib/documents"

// One tab can be backed by more than one real backend status (`ВІДПРАВЛЕНО`
// covers both `SENT` and `DELIVERED` — see `lib/documents.ts`'s
// `documentTabStatusToBackend`), and `GET /otk/documents` only accepts one
// status per request — so a tab tracks one cursor/hasMore pair per backend
// status it's made of, rather than a single one.
interface StreamState {
  cursor: string | null
  hasMore: boolean
}

interface TabState {
  documents: DocumentRow[]
  streams: Partial<Record<BackendDocumentStatus, StreamState>>
  isLoading: boolean
  isLoadingMore: boolean
  error: string | null
  fetched: boolean
  // The `sort` page 1 was fetched with — `fetchTab` compares this against
  // the sort currently requested and refetches instead of trusting `fetched`
  // when they differ (an already-loaded page's cursor and row order are
  // both tied to the sort direction they were fetched under).
  fetchedSort: Sort | null
}

function emptyTabState(status: DocumentTabStatus): TabState {
  return {
    documents: [],
    streams: Object.fromEntries(
      documentTabStatusToBackend[status].map((backendStatus) => [
        backendStatus,
        { cursor: null, hasMore: false },
      ])
    ),
    isLoading: false,
    isLoadingMore: false,
    error: null,
    fetched: false,
    fetchedSort: null,
  }
}

function initialByStatus(): Record<DocumentTabStatus, TabState> {
  return Object.fromEntries(
    documentTabStatuses.map((status) => [status, emptyTabState(status)])
  ) as Record<DocumentTabStatus, TabState>
}

// Applies `transform` to every tab bucket — used by the mutations below,
// which don't know (and shouldn't have to be told) which tab a given
// document id currently lives in.
function mapByStatus(
  byStatus: Record<DocumentTabStatus, TabState>,
  transform: (tabState: TabState) => TabState
): Record<DocumentTabStatus, TabState> {
  return Object.fromEntries(
    documentTabStatuses.map((status) => [status, transform(byStatus[status])])
  ) as Record<DocumentTabStatus, TabState>
}

interface DocumentsState {
  byStatus: Record<DocumentTabStatus, TabState>
  counts: OtkDocumentCounts | null
  refreshCounts: () => Promise<void>
  // `POST /otk/documents/:id/retry` — re-delivers the already-signed package
  // (no re-signing, documents.md). The backend answers with the document's
  // own new state, so the affected tabs are refetched rather than patched
  // optimistically: a successful retry moves the row to a different tab
  // entirely, and a failed one leaves it where it is with a fresh retry
  // window.
  retrySend: (id: string, sort: Sort) => Promise<void>
  // otk-documents.ts's `GET /otk/documents` — one request per backend status
  // making up the tab currently on screen. Counts use a separate capped
  // request while rows remain paginated and fetched lazily.
  // Cached per tab (`fetched`), so switching back to an already-loaded tab
  // doesn't refetch.
  // `sort` drives the real backend order (`GET /otk/documents`'s own `sort`
  // param, `createdAt` only — see `documentsListQuery`) rather than a
  // client-side re-sort of whatever page happens to be loaded; every other
  // column (`Підписано`, `Тип`, …) still sorts client-side via `DataTable`'s
  // own `getSortedRowModel`. Callers own where `sort` itself is stored
  // (`Documents.tsx`'s `?sort=` query param); the store only ever receives
  // it as an argument, so a tab fetched under one sort and revisited under
  // another naturally refetches instead of serving a stale order.
  fetchTab: (status: DocumentTabStatus, sort: Sort) => Promise<void>
  // Forces a fresh page-1 fetch regardless of `fetched` — used after a
  // mutation the store itself doesn't know how to apply optimistically
  // (creating a new draft via `NewDocumentButton`, whose own row exists
  // only on the server after `POST /otk/documents/create`).
  refetchTab: (status: DocumentTabStatus, sort: Sort) => Promise<void>
  // Fetches the next page (per backend status still `hasMore`) for an
  // already-loaded tab, appending to what's there — the "Завантажити ще"
  // button's own handler.
  loadMore: (status: DocumentTabStatus, sort: Sort) => Promise<void>
  // `deleteDraft` calls the real `DELETE /otk/documents/:id` (see
  // `deleteOtkDocument`) and only drops the row locally once the server
  // confirms it. `annul` still has no backend endpoint (only
  // list/get/delete/patch/create/nonconformities/reconstructions exist in
  // `otk-documents.ts`), so it stays a local-only mutation against whichever
  // tab bucket already holds the row, same precedent as `SubjectUsers.tsx`'s
  // `onCancelInvite` staying local-only until a real cancel-invite endpoint
  // exists.
  deleteDraft: (id: string, version: number) => Promise<void>
  // There is no dedicated duplicate endpoint either, but unlike `annul` this
  // doesn't need one: it creates a real draft via `POST /otk/documents/create`
  // (the same call `NewDocumentButton` makes) and copies the selected
  // sections onto it via the existing `PATCH`/`PUT .../nonconformities`
  // endpoints — see `documentEditor.ts`'s `buildDuplicatePatchOperations`/
  // `buildDuplicateNonconformitiesPayload`. Resolves to the new draft's id so
  // the caller can navigate straight into its editor, per documents.md.
  duplicate: (id: string, sectionIds: string[], sort: Sort) => Promise<string>
  annul: (id: string, reason: string) => void
}

function errorMessage(err: unknown): string {
  console.error(err);
  const code = (err as { response?: { data?: { error?: string } } })?.response
    ?.data?.error
  if (code === "SUBJECT_NOT_CONNECTED") {
    return "Обліковий запис ще не пов'язаний із суб'єктом ОТК."
  }
  if (code === "FORBIDDEN") {
    return "Немає доступу до перегляду документів."
  }
  return "Не вдалося завантажити документи."
}

function findTabOf(
  byStatus: Record<DocumentTabStatus, TabState>,
  id: string
): DocumentTabStatus | undefined {
  return documentTabStatuses.find((status) =>
    byStatus[status].documents.some((document) => document.id === id)
  )
}

// Shared by `fetchTab`/`refetchTab` — always fetches a fresh page 1 for
// every backend status the tab is made of, in parallel, and merges the
// results. Each request already asks the backend for `sort` order; the
// `sortDocumentsByCreated` pass below only has to interleave those
// already-sorted streams, not establish the order itself (see its own doc
// comment in `lib/documents.ts`).
async function loadTab(
  status: DocumentTabStatus,
  sort: Sort,
  set: (
    fn: (state: DocumentsState) => Partial<DocumentsState>
  ) => void
) {
  set((state) => ({
    byStatus: {
      ...state.byStatus,
      [status]: { ...state.byStatus[status], isLoading: true, error: null },
    },
  }))
  try {
    const backendStatuses = documentTabStatusToBackend[status]
    const pages = await Promise.all(
      backendStatuses.map((backendStatus) =>
        getOtkDocumentsPage({ status: backendStatus, sort })
      )
    )
    const documents = sortDocumentsByCreated(
      pages.flatMap((page) =>
        page.data.map((view) => mapOtkDocumentView(view, status))
      ),
      sort
    )
    const streams: TabState["streams"] = {}
    backendStatuses.forEach((backendStatus, index) => {
      const page = pages[index]
      streams[backendStatus] = {
        cursor: page.nextPage,
        hasMore: page.nextPage !== null,
      }
    })
    set((state) => ({
      byStatus: {
        ...state.byStatus,
        [status]: {
          ...state.byStatus[status],
          documents,
          streams,
          isLoading: false,
          fetched: true,
          fetchedSort: sort,
        },
      },
    }))
  } catch (err) {
    set((state) => ({
      byStatus: {
        ...state.byStatus,
        [status]: { ...state.byStatus[status], isLoading: false, error: errorMessage(err) },
      },
    }))
  }
}

// Whether the "Завантажити ще" button should show — any backend status
// making up the tab still has another page. Exported as a plain function
// (not baked into `TabState` itself) since it's a one-line derivation, not
// state that needs its own set/get lifecycle.
export function tabHasMore(tabState: TabState): boolean {
  return Object.values(tabState.streams).some((stream) => stream?.hasMore)
}

export const useDocumentsStore = create<DocumentsState>((set, get) => ({
  byStatus: initialByStatus(),
  counts: null,

  refreshCounts: async () => {
    try {
      set({counts: await getOtkDocumentCounts()})
    } catch (err) {
      // Keep the previous counts if refresh fails.
      console.error(err)
    }
  },

  retrySend: async (id, sort) => {
    try {
      await retryOtkDocument(id)
    } finally {
      // A refused or failed attempt still moved the row's `nextRetryAt`, so
      // the list is reloaded either way — the error is rethrown afterwards.
      await Promise.all([
        loadTab("НЕ ВІДПРАВЛЕНО", sort, set),
        loadTab("ВІДПРАВЛЕНО", sort, set),
      ])
      await get().refreshCounts()
    }
  },

  fetchTab: async (status, sort) => {
    const current = get().byStatus[status]
    if ((current.fetched && current.fetchedSort === sort) || current.isLoading) return
    await loadTab(status, sort, set)
  },

  refetchTab: async (status, sort) => {
    if (get().byStatus[status].isLoading) return
    await loadTab(status, sort, set)
  },

  loadMore: async (status, sort) => {
    const current = get().byStatus[status]
    const streamsWithMore = (
      Object.entries(current.streams) as [BackendDocumentStatus, StreamState][]
    ).filter(([, stream]) => stream.hasMore && stream.cursor)
    if (streamsWithMore.length === 0 || current.isLoadingMore) return
    set((state) => ({
      byStatus: { ...state.byStatus, [status]: { ...current, isLoadingMore: true } },
    }))
    try {
      const pages = await Promise.all(
        streamsWithMore.map(([backendStatus, stream]) =>
          getOtkDocumentsPage({ status: backendStatus, cursor: stream.cursor!, sort })
        )
      )
      const newDocuments = pages.flatMap((page) =>
        page.data.map((view) => mapOtkDocumentView(view, status))
      )
      const nextStreams: TabState["streams"] = { ...current.streams }
      streamsWithMore.forEach(([backendStatus], index) => {
        const page = pages[index]
        nextStreams[backendStatus] = {
          cursor: page.nextPage,
          hasMore: page.nextPage !== null,
        }
      })
      set((state) => ({
        byStatus: {
          ...state.byStatus,
          [status]: {
            ...state.byStatus[status],
            documents: sortDocumentsByCreated(
              [...state.byStatus[status].documents, ...newDocuments],
              sort
            ),
            streams: nextStreams,
            isLoadingMore: false,
          },
        },
      }))
    } catch (err) {
      set((state) => ({
        byStatus: {
          ...state.byStatus,
          [status]: { ...state.byStatus[status], isLoadingMore: false, error: errorMessage(err) },
        },
      }))
    }
  },

  deleteDraft: async (id, version) => {
    await deleteOtkDocument(id, version)
    set((state) => ({
      byStatus: mapByStatus(state.byStatus, (tabState) => ({
        ...tabState,
        documents: tabState.documents.filter((document) => document.id !== id),
      })),
    }))
    await get().refreshCounts()
  },

  // Creates a real draft, then copies the selected sections onto it from
  // the source document's own fetched detail. `testResults` never fails
  // validation (its schema fields are all plain numbers/booleans/nullable),
  // but `nonconformities` is a separate call (`PUT .../nonconformities`
  // replaces the whole list, distinct from the generic per-field `PATCH`)
  // and only fires when that section was actually selected and the source
  // has something to copy — an empty `PUT` would just reset `hasNonconformities`
  // to `false` on a draft that has nothing to reset yet.
  duplicate: async (id, sectionIds, sort) => {
    const [created, source] = await Promise.all([
      createOtkDocument(),
      getOtkDocumentById(id),
    ])
    let version = created.version
    const operations = buildDuplicatePatchOperations(source, sectionIds)
    if (operations.length > 0) {
      const patched = await patchOtkDocument(created.id, {
        expectedVersion: version,
        operations,
      })
      version = patched.version
    }
    if (sectionIds.includes("nonconformities")) {
      const payload = buildDuplicateNonconformitiesPayload(source)
      if (payload.items.length > 0) {
        await saveOtkDocumentNonconformities(created.id, {
          expectedVersion: version,
          // `items.length > 0` always implies `true` server-side
          // (`saveNonconformitiesBodySchema`'s superRefine rejects the
          // opposite combination) — not `payload.hasNonconformities`, which
          // only reflects the source's own possibly-stale flag.
          hasNonconformities: true,
          items: payload.items,
        })
      }
    }
    await get().refetchTab("ЧЕРНЕТКА", sort)
    await get().refreshCounts()
    return created.id
  },

  // `reason` isn't modeled on `DocumentRow` either, for the same reason —
  // accepted to match `AnnulDialog`'s callback signature.
  annul: (id) =>
    set((state) => {
      const sourceTab = findTabOf(state.byStatus, id)
      if (!sourceTab) return state
      const source = state.byStatus[sourceTab].documents.find(
        (document) => document.id === id
      )!
      const annulled: DocumentRow = { ...source, status: "АНУЛЬОВАНО" }
      return {
        byStatus: {
          ...state.byStatus,
          [sourceTab]: {
            ...state.byStatus[sourceTab],
            documents: state.byStatus[sourceTab].documents.filter(
              (document) => document.id !== id
            ),
          },
          АНУЛЬОВАНО: {
            ...state.byStatus.АНУЛЬОВАНО,
            documents: [annulled, ...state.byStatus.АНУЛЬОВАНО.documents],
          },
        },
      }
    }),
}))
